import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Table, Button, Form, Space, Row, Col, Breadcrumb, Modal,
    message, Typography, Select, DatePicker, Input, Tag, Descriptions, Dropdown,
    InputNumber, Divider
} from "antd";
import {
    PlusOutlined, HomeOutlined, FilterOutlined, ClearOutlined, SearchOutlined,
    EyeOutlined, EditOutlined, DeleteOutlined, MoreOutlined, ThunderboltOutlined,
    PlayCircleOutlined, CheckCircleOutlined, ProfileOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../../services/axiosInstance";
import { isPermissionAllowed } from "../../../utils/permissionHelper";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

const HARVESTB_STATUS_OPTIONS = [
    { value: "Pending", label: "Pending" },
    { value: "Ongoing", label: "Ongoing" },
    { value: "Completed", label: "Completed" },
];

const HARVESTB_STATUS_COLORS = {
    "Pending": "orange",
    "Ongoing": "blue",
    "Completed": "green",
};

const formatLKR = (value) =>
    `LKR ${parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ManageHarvestBatches = () => {
    const navigate = useNavigate();
    const [batches, setBatches] = useState([]);
    const [lands, setLands] = useState([]);
    const [seasons, setSeasons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [viewVisible, setViewVisible] = useState(false);
    const [viewBatch, setViewBatch] = useState(null);
    const [editingBatch, setEditingBatch] = useState(null);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    // Advanced filter state
    const [search, setSearch] = useState("");
    const [filterLand, setFilterLand] = useState(undefined);
    const [filterSection, setFilterSection] = useState(undefined);
    const [filterStatus, setFilterStatus] = useState(undefined);
    const [filterSeason, setFilterSeason] = useState(undefined);
    const [applied, setApplied] = useState({});

    // Form state for cascading land->section
    const [formLand, setFormLand] = useState(undefined);
    const [formSections, setFormSections] = useState([]);
    const [editFormLand, setEditFormLand] = useState(undefined);
    const [editFormSections, setEditFormSections] = useState([]);

    const fetchRefData = useCallback(async () => {
        try {
            const landRes = await api.get("/masterdropdown/listof_activelands.php");
            setLands(landRes.data.dataset || []);
            const seasonRes = await api.get("/masterdropdown/listof_activeseasons.php");
            setSeasons(seasonRes.data.dataset || []);
        } catch {
            message.error("Failed to load reference data.");
        }
    }, []);

    const fetchSections = useCallback(async (landId, setter) => {
        if (!landId) { setter([]); return; }
        try {
            const res = await api.get(`/masterdropdown/listof_activesections.php?land_id=${landId}`);
            setter(res.data.dataset || []);
        } catch {
            setter([]);
        }
    }, []);

    const fetchBatches = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
        setLoading(true);
        try {
            const res = await api.post("/harvesting/list.php", {
                page,
                limit: pageSize,
                search: filters.search || "",
                land_id: filters.land_id || 0,
                section_id: filters.section_id || 0,
                status: filters.status || "",
                season: filters.season || "",
            });
            setBatches(res.data.dataset || []);
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch harvest batches.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRefData();
        fetchBatches(1, 10, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilter = () => {
        const filters = {
            search: search.trim(),
            land_id: filterLand,
            section_id: filterSection,
            status: filterStatus,
            season: filterSeason,
        };
        setApplied(filters);
        fetchBatches(1, pagination.pageSize, filters);
    };

    const handleClear = () => {
        setSearch("");
        setFilterLand(undefined);
        setFilterSection(undefined);
        setFilterStatus(undefined);
        setFilterSeason(undefined);
        setApplied({});
        fetchBatches(1, pagination.pageSize, {});
    };

    const openView = async (record) => {
        setViewBatch(record);
        setViewVisible(true);
        try {
            const res = await api.get(`/harvesting/detail.php?id=${record.harvestb_id}`);
            if (res.data.status === "success") setViewBatch(res.data);
        } catch {
            message.error("Failed to load harvest batch details.");
        }
    };

    const openEdit = async (record) => {
        setEditingBatch(record);
        // Load sections for the current land
        if (record.land_id) {
            await fetchSections(record.land_id, setEditFormSections);
            setEditFormLand(record.land_id);
        }
        editForm.setFieldsValue({
            landId: Number(record.land_id),
            sectionId: Number(record.section_id),
            targetQty: record.target_qty,
            targetKg: record.target_kg,
            harvestSeason: record.harvest_season,
            targetStartDate: record.harvest_start_date ? dayjs(record.harvest_start_date) : undefined,
            targetEndDate: record.harvest_end_date ? dayjs(record.harvest_end_date) : undefined,
            costingRate: record.costing_rate,
            sellingRate: record.selling_rate,
        });
        setIsEditModalVisible(true);
    };

    const handleEdit = async (values) => {
        setLoading(true);
        try {
            const res = await api.post("/harvesting/edit.php", {
                harvestb_id: editingBatch.harvestb_id,
                land_id: values.landId,
                section_id: values.sectionId,
                target_qty: values.targetQty,
                target_kg: values.targetKg,
                harvest_season: values.harvestSeason,
                harvest_start_date: values.targetStartDate ? values.targetStartDate.format("YYYY-MM-DD") : "",
                harvest_end_date: values.targetEndDate ? values.targetEndDate.format("YYYY-MM-DD") : "",
                costing_rate: values.costingRate || 0,
                selling_rate: values.sellingRate || 0,
            });
            if (res.data.status === "success") {
                message.success(res.data.message);
                setIsEditModalVisible(false);
                editForm.resetFields();
                handleFilter();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update harvest batch.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: "Delete Harvest Batch",
            content: `Are you sure you want to delete harvest batch ${record.harvestb_ref}? This action cannot be undone.`,
            okText: "Yes, Delete",
            okType: "danger",
            cancelText: "Cancel",
            maskClosable: false,
            onOk: async () => {
                setLoading(true);
                try {
                    const res = await api.post("/harvesting/delete.php", { harvestb_id: record.harvestb_id });
                    if (res.data.status === "success") {
                        message.success(res.data.message);
                        handleFilter();
                    } else throw new Error(res.data.message);
                } catch (err) {
                    message.error(err?.response?.data?.message || "Failed to delete harvest batch.");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleStartProcessing = (record) => {
        Modal.confirm({
            title: "Start Processing",
            content: `Start processing harvest batch ${record.harvestb_ref}? The status will change to Ongoing.`,
            okText: "Yes, Start",
            okType: "primary",
            cancelText: "Cancel",
            maskClosable: false,
            onOk: async () => {
                setLoading(true);
                try {
                    const res = await api.post("/harvesting/start.php", { harvestb_id: record.harvestb_id });
                    if (res.data.status === "success") {
                        message.success(res.data.message);
                        handleFilter();
                    } else throw new Error(res.data.message);
                } catch (err) {
                    message.error(err?.response?.data?.message || "Failed to start processing.");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleCloseProcessing = (record) => {
        Modal.confirm({
            title: "Close Processing",
            content: `Close processing for harvest batch ${record.harvestb_ref}? The status will change to Completed.`,
            okText: "Yes, Close",
            okType: "primary",
            cancelText: "Cancel",
            maskClosable: false,
            onOk: async () => {
                setLoading(true);
                try {
                    const res = await api.post("/harvesting/close.php", { harvestb_id: record.harvestb_id });
                    if (res.data.status === "success") {
                        message.success(res.data.message);
                        handleFilter();
                    } else throw new Error(res.data.message);
                } catch (err) {
                    message.error(err?.response?.data?.message || "Failed to close processing.");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleAdd = async (values) => {
        setLoading(true);
        try {
            const res = await api.post("/harvesting/create.php", {
                land_id: values.landId,
                section_id: values.sectionId,
                target_qty: values.targetQty,
                target_kg: values.targetKg,
                harvest_season: values.harvestSeason,
                harvest_start_date: values.targetStartDate ? values.targetStartDate.format("YYYY-MM-DD") : "",
                harvest_end_date: values.targetEndDate ? values.targetEndDate.format("YYYY-MM-DD") : "",
                costing_rate: values.costingRate || 0,
                selling_rate: values.sellingRate || 0,
                harvestb_status: "Pending",
            });
            if (res.data.status === "success") {
                message.success(`${res.data.message} (${res.data.harvestb_ref})`);
                setIsModalVisible(false);
                form.resetFields();
                setFormLand(undefined);
                setFormSections([]);
                handleFilter();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to create harvest batch.");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: "Batch Ref",
            dataIndex: "harvestb_ref",
            key: "harvestb_ref",
            render: (v) => <Typography.Text strong>{v}</Typography.Text>,
        },
        {
            title: "Land",
            dataIndex: "land_name",
            key: "land_name",
            render: (v) => v || "-",
        },
        {
            title: "Section",
            dataIndex: "section_name",
            key: "section_name",
            render: (v) => v || "-",
        },
        {
            title: "Season",
            dataIndex: "harvest_season",
            key: "harvest_season",
            render: (v) => v || "-",
        },
        {
            title: "Target (Qty/Kg)",
            key: "target",
            render: (_, r) => (
                <span>
                    {Number(r.target_qty || 0).toLocaleString()} / {Number(r.target_kg || 0).toLocaleString()} kg
                </span>
            ),
        },
        {
            title: "Collected (Qty/Kg)",
            key: "collected",
            render: (_, r) => (
                <span>
                    {Number(r.total_collect_qty || 0).toLocaleString()} / {Number(r.total_collect_kg || 0).toLocaleString()} kg
                </span>
            ),
        },
        {
            title: "Target Start",
            dataIndex: "harvest_start_date",
            key: "harvest_start_date",
            align: "center",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
        },
        {
            title: "Target End",
            dataIndex: "harvest_end_date",
            key: "harvest_end_date",
            align: "center",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
        },
        {
            title: "Status",
            dataIndex: "harvestb_status",
            key: "harvestb_status",
            align: "center",
            render: (s) => (
                <Tag color={HARVESTB_STATUS_COLORS[s] || "default"} style={{ fontWeight: 600 }}>{s || "-"}</Tag>
            ),
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            fixed: "right",
            width: 90,
            render: (_, record) => {
                const status = record.harvestb_status;
                const canViewDetails = isPermissionAllowed('harvesting_harvest_batches_view_details');
                const canEdit = isPermissionAllowed('harvesting_harvest_batches_edit');
                const canDelete = isPermissionAllowed('harvesting_harvest_batches_delete');
                const items = [
                    ...(canViewDetails ? [{
                        key: "summary", label: "View Summary", icon: <EyeOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openView(record); },
                    }] : []),
                    ...(canViewDetails ? [{
                        key: "full-details", label: "View Full Details", icon: <ProfileOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); navigate(`/harvesting/harvest-batches/${record.harvestb_id}`); },
                    }] : []),
                    ...(canEdit && status === "Pending" ? [{
                        key: "start", label: "Start Processing", icon: <PlayCircleOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); handleStartProcessing(record); },
                    }] : []),
                    ...(canEdit && status === "Ongoing" ? [{
                        key: "close", label: "Close Processing", icon: <CheckCircleOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); handleCloseProcessing(record); },
                    }] : []),
                    ...(canEdit ? [{
                        key: "edit", label: "Edit", icon: <EditOutlined />, disabled: status !== "Pending",
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openEdit(record); },
                    }] : []),
                    ...(canDelete ? [{
                        key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, disabled: status !== "Pending",
                        onClick: (info) => { info?.domEvent?.stopPropagation(); handleDelete(record); },
                    }] : []),
                ];
                if (items.length === 0) return null;
                return (
                    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                        <Button type="text" className="fluid-dots-btn" icon={<MoreOutlined />} />
                    </Dropdown>
                );
            },
        },
    ];

    const renderBatchForm = (currentForm, onSubmit, onLandChange, selectedLand, formSectionsList, buttonLabel, loadingProp, onCancel) => (
        <Form form={currentForm} layout="vertical" onFinish={onSubmit}>
            <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                    <Form.Item label="Land" name="landId" rules={[{ required: true, message: "Land is required" }]}>
                        <Select
                            allowClear
                            placeholder="Select Land"
                            showSearch
                            optionFilterProp="children"
                            onChange={(val) => { onLandChange(val); currentForm.setFieldsValue({ sectionId: undefined }); }}
                        >
                            {lands.map((l) => <Option key={l.value} value={Number(l.value)}>{l.label}</Option>)}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item label="Section" name="sectionId" rules={[{ required: true, message: "Section is required" }]}>
                        <Select
                            allowClear
                            placeholder="Select Section"
                            showSearch
                            optionFilterProp="children"
                        >
                            {formSectionsList.map((s) => <Option key={s.value} value={Number(s.value)}>{s.label}</Option>)}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                    <Form.Item label="Harvest Season" name="harvestSeason" rules={[{ required: true, message: "Season is required" }]}>
                        <Select
                            allowClear
                            placeholder="Select Season"
                            showSearch
                            optionFilterProp="children"
                        >
                            {seasons.map((s) => <Option key={s.value} value={s.label}>{s.label}</Option>)}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item label="Target Quantity" name="targetQty" rules={[{ required: true, message: "Target quantity is required" }]}>
                        <InputNumber style={{ width: "100%" }} min={0} placeholder="e.g. 500" />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                    <Form.Item label="Target Weight (Kg)" name="targetKg" rules={[{ required: true, message: "Target weight is required" }]}>
                        <InputNumber style={{ width: "100%" }} min={0} step={0.01} placeholder="e.g. 2500.00" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item label="Costing Rate (per Kg)" name="costingRate" rules={[{ required: true, message: "Costing rate is required" }]}>
                        <InputNumber style={{ width: "100%" }} min={0.01} step={0.01} placeholder="0.00" />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                    <Form.Item label="Selling Rate (per Kg)" name="sellingRate" rules={[{ required: true, message: "Selling rate is required" }]}>
                        <InputNumber style={{ width: "100%" }} min={0.01} step={0.01} placeholder="0.00" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item label="Target Start Date" name="targetStartDate">
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                    <Form.Item label="Target End Date" name="targetEndDate">
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item label="Status" style={{ marginBottom: 0 }}>
                        <Tag color={HARVESTB_STATUS_COLORS["Pending"]} style={{ fontWeight: 600 }}>Pending</Tag>
                        <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                            New batches start in Pending status and can be started from the batch list.
                        </Typography.Text>
                    </Form.Item>
                </Col>
            </Row>
            <Row justify="end">
                <Form.Item style={{ marginBottom: 0 }}>
                    <Space>
                        <Button danger onClick={() => { onCancel(); currentForm.resetFields(); }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loadingProp}>
                            {buttonLabel}
                        </Button>
                    </Space>
                </Form.Item>
            </Row>
        </Form>
    );

    // Calculated values for the view modal
    const viewCostingRate = viewBatch ? parseFloat(viewBatch.costing_rate || 0) : 0;
    const viewSellingRate = viewBatch ? parseFloat(viewBatch.selling_rate || 0) : 0;
    const viewTargetKg = viewBatch ? parseFloat(viewBatch.target_kg || 0) : 0;
    const totalTargetCost = viewCostingRate * viewTargetKg;
    const estimatedRevenue = viewSellingRate * viewTargetKg;
    const estimatedProfit = estimatedRevenue - totalTargetCost;

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Harvesting' },
                                { title: 'Harvest Batches' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <ThunderboltOutlined /> Harvest Batches
                        </h1>
                        <p className="fluid-page-subtitle">Manage harvest batches - track targets, collection and quality</p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            {isPermissionAllowed('harvesting_harvest_batches_create') && (
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsModalVisible(true)}
                                    className="hero-action-btn"
                                >
                                    Create New Batch
                                </Button>
                            )}
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    {/* Advanced filter bar */}
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={4}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Ref, season"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={3}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Land:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterLand}
                                    onChange={setFilterLand}
                                    placeholder="All Lands"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {lands.map((l) => <Option key={l.value} value={Number(l.value)}>{l.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={3}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Status:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterStatus}
                                    onChange={setFilterStatus}
                                    placeholder="All Statuses"
                                    allowClear
                                >
                                    {HARVESTB_STATUS_OPTIONS.map((s) => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={3}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Season:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterSeason}
                                    onChange={setFilterSeason}
                                    placeholder="All Seasons"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {seasons.map((s) => <Option key={s.value} value={s.label}>{s.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={24} lg={5}>
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={batches}
                            rowKey="harvestb_id"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={(pg) => fetchBatches(pg.current, pg.pageSize, applied)}
                            loading={loading}
                            scroll={{ x: 1300 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            {/* Create Modal */}
            <Modal
                title="Create New Harvest Batch"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); form.resetFields(); setFormLand(undefined); setFormSections([]); }}
                footer={null}
                width={780}
                destroyOnClose
            >
                {renderBatchForm(
                    form,
                    handleAdd,
                    (val) => { setFormLand(val); fetchSections(val, setFormSections); },
                    formLand,
                    formSections,
                    "Create Batch",
                    loading,
                    () => { setIsModalVisible(false); setFormLand(undefined); setFormSections([]); }
                )}
            </Modal>

            {/* View Modal */}
            <Modal
                title={viewBatch ? `Harvest Batch Summary - ${viewBatch.harvestb_ref}` : "Harvest Batch Summary"}
                open={viewVisible}
                onCancel={() => setViewVisible(false)}
                footer={<Button type="primary" onClick={() => setViewVisible(false)}>Close</Button>}
                width={820}
                maskClosable={false}
                destroyOnClose
            >
                {viewBatch && (
                    <>
                        <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                            Batch Information
                        </Typography.Title>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="Batch Ref" span={2}>
                                <Typography.Text strong>{viewBatch.harvestb_ref}</Typography.Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Land">{viewBatch.land_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Section">{viewBatch.section_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Harvest Season">{viewBatch.harvest_season || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={HARVESTB_STATUS_COLORS[viewBatch.harvestb_status] || "default"} style={{ fontWeight: 600 }}>
                                    {viewBatch.harvestb_status || "-"}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Target Quantity">{Number(viewBatch.target_qty || 0).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="Target Weight (Kg)">{Number(viewBatch.target_kg || 0).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="Target Start Date">
                                {viewBatch.harvest_start_date ? dayjs(viewBatch.harvest_start_date).format("YYYY-MM-DD") : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Target End Date">
                                {viewBatch.harvest_end_date ? dayjs(viewBatch.harvest_end_date).format("YYYY-MM-DD") : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Costing Rate (per Kg)">{formatLKR(viewBatch.costing_rate)}</Descriptions.Item>
                            <Descriptions.Item label="Selling Rate (per Kg)">{formatLKR(viewBatch.selling_rate)}</Descriptions.Item>
                            <Descriptions.Item label="Created By">{viewBatch.creator_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Created Date">
                                {viewBatch.cr_dt ? dayjs(viewBatch.cr_dt).format("YYYY-MM-DD HH:mm") : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Last Updated By">{viewBatch.checker_name || "-"}</Descriptions.Item>
                            <Descriptions.Item label="Last Updated Date">
                                {viewBatch.ch_dt ? dayjs(viewBatch.ch_dt).format("YYYY-MM-DD HH:mm") : "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider style={{ margin: "16px 0" }} />

                        <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                            Collection & Financial Summary
                        </Typography.Title>
                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="Total Collected Quantity">{Number(viewBatch.total_collect_qty || 0).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="Total Collected Weight (Kg)">{Number(viewBatch.total_collect_kg || 0).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="Quality Reject Quantity">{Number(viewBatch.quality_reject_qty || 0).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="Quality Reject Weight (Kg)">{Number(viewBatch.quality_reject_kg || 0).toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="Total Target Cost">
                                <Typography.Text strong>{formatLKR(totalTargetCost)}</Typography.Text>
                                <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                                    {viewCostingRate.toFixed(2)} × {Number(viewTargetKg).toLocaleString()} kg
                                </Typography.Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Estimated Revenue">
                                <Typography.Text strong>{formatLKR(estimatedRevenue)}</Typography.Text>
                                <Typography.Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                                    {viewSellingRate.toFixed(2)} × {Number(viewTargetKg).toLocaleString()} kg
                                </Typography.Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Estimated Profit" span={2}>
                                <Typography.Text
                                    strong
                                    style={{ fontSize: 16, color: estimatedProfit >= 0 ? "#3f8600" : "#cf1322" }}
                                >
                                    {formatLKR(estimatedProfit)}
                                </Typography.Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </>
                )}
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Harvest Batch"
                open={isEditModalVisible}
                onCancel={() => { setIsEditModalVisible(false); editForm.resetFields(); setEditFormLand(undefined); setEditFormSections([]); }}
                footer={null}
                width={780}
                maskClosable={false}
                destroyOnClose
            >
                {renderBatchForm(
                    editForm,
                    handleEdit,
                    (val) => { setEditFormLand(val); fetchSections(val, setEditFormSections); },
                    editFormLand,
                    editFormSections,
                    "Update Batch",
                    loading,
                    () => { setIsEditModalVisible(false); setEditFormLand(undefined); setEditFormSections([]); }
                )}
            </Modal>
        </Layout>
    );
};

export default ManageHarvestBatches;
